import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
// import { this.appInsights } from 'applicationinsights-js';
import {
  ApplicationInsights,
  IConfig,
  SeverityLevel,
  IPageViewTelemetry,
  IExceptionTelemetry,
  IConfiguration,
} from '@microsoft/applicationinsights-web';
import { Subscription, firstValueFrom } from 'rxjs';
import { Router, ActivatedRouteSnapshot, ResolveEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { ConfigService } from './config.service';
import { EventFilter } from '@models/eventFilter.model';

/*
    Service/pattern created based on https://blogs.msdn.microsoft.com/premier_developer/2017/05/11/add-application-insights-to-an-angular-spa/

    If we decide to use more of the built in approach and not our own cusotm telemetry, we could follow adding the scripts dynamically to the head of the page
    that is outlined in the link below. A quick test didn't look like what we'd want though
    https://medium.com/@umashankar.itn/angular-apps-monitoring-using-azure-application-insights-e25a55fe49d5
*/
@Injectable()
export class MonitoringService {
  private _appInsightsConfigured: boolean = null;
  private _routerSubscription: Subscription;

  private config: IConfiguration & IConfig = null;
  private appInsights: ApplicationInsights = null;
  constructor(private router: Router, private http: HttpClient, private configService: ConfigService) {}

  public setupMonitoring(buildVersionNumber: string) {
    this.configureAppInsights(buildVersionNumber).then(() => {
      this._routerSubscription = this.router.events.pipe(filter((event) => event instanceof ResolveEnd)).subscribe((event: ResolveEnd) => {
        const activatedComponent = this.getActivatedComponent(event.state.root);
        if (activatedComponent) {
          this.logPageView(`${activatedComponent.name} ${this.getRouteTemplate(event.state.root)}`, event.urlAfterRedirects);
        }
      });
    });
  }

  public logPageView(name?: string, url?: string, properties?: any, measurements?: any, duration?: number) {
    if (this._appInsightsConfigured === true) {
      const pageViewobj: IPageViewTelemetry = {
        name: name,
        uri: url,
        properties: { duration: duration, ...this.addGlobalProperties(properties), ...measurements },
      };
      this.appInsights.trackPageView(pageViewobj);
    }
  }

  public logPageStart(name?: string): any {
    if (this._appInsightsConfigured === true) {
      this.appInsights.startTrackPage(name);
    }
  }

  public stopTrackPage(name?: string, url?: string, properties?: { [name: string]: string }, measurements?: { [name: string]: number }): any {
    if (this._appInsightsConfigured === true) {
      this.appInsights.stopTrackPage(name, url, this.addGlobalProperties(properties), measurements);
    }
  }

  public logEvent(name: string, properties?: any, measurements?: any) {
    if (this._appInsightsConfigured === true) {
      this.appInsights.trackEvent({ name: name }, { ...this.addGlobalProperties(properties), ...measurements });
    }
  }

  public logEventStart(name: string): any {
    if (this._appInsightsConfigured === true) {
      this.appInsights.startTrackEvent(name);
    }
  }

  public logEventEnd(name: string, properties?: { [name: string]: string }, measurements?: { [name: string]: number }): any {
    if (this._appInsightsConfigured === true) {
      this.appInsights.stopTrackEvent(name, this.addGlobalProperties(properties), measurements);
    }
  }

  public logErrorMessage(
    message: string,
    handledAt?: string,
    properties?: { [name: string]: string },
    measurements?: { [name: string]: number },
    severityLevel?: SeverityLevel
  ): any {
    this.logException(new Error(message), handledAt, properties, measurements, severityLevel);
  }

  public logException(
    error: Error,
    handledAt?: string,
    properties?: { [name: string]: string },
    measurements?: { [name: string]: number },
    severityLevel?: SeverityLevel
  ): any {
    if (this._appInsightsConfigured === true) {
      const exceptionTelemetryObj: IExceptionTelemetry = {
        exception: error,
        severityLevel: severityLevel,
      };
      this.appInsights.trackException(exceptionTelemetryObj, { ...this.addGlobalProperties(properties), ...measurements });
    }
  }

  public setAuthenticatedUserContext(authenticatedUserId: string, accountId?: string): any {
    if (this._appInsightsConfigured === true) {
      this.appInsights.setAuthenticatedUserContext(authenticatedUserId, accountId, true);
    }
  }

  public clearAuthenticatedUserContext(): any {
    if (this._appInsightsConfigured === true) {
      this.appInsights.clearAuthenticatedUserContext();
    }
  }

  public async getEvents(eventFilter: EventFilter): Promise<any> {
    const results = await firstValueFrom(
      this.configService.getConfigValues([ConfigService.APPINSIGHTS_RESTAPI_APPID, ConfigService.APPINSIGHTS_RESTAPI_APIKEY])
    );

    const restAPIAppId = results[ConfigService.APPINSIGHTS_RESTAPI_APPID];
    const restAPIAppKey = results[ConfigService.APPINSIGHTS_RESTAPI_APIKEY];

    let restAPI = `https://api.applicationinsights.io/v1/apps/${restAPIAppId}/events/${eventFilter.type}?`;
    restAPI += `$top=${eventFilter.pageSize}`;
    restAPI += `&$orderBy=timestamp desc`;
    restAPI += `&$count=true`;

    if (eventFilter.searchCriteria && eventFilter.searchCriteria.length > 0) {
      restAPI += `&$search=${eventFilter.searchCriteria}`;
    }

    let filter = '';
    if (eventFilter.authenticatedUserId && eventFilter.authenticatedUserId.length > 0) {
      filter = `&$filter=user/authenticatedId eq '${eventFilter.authenticatedUserId}'`;
    }

    if (eventFilter.endDateRange) {
      const endDateFilter = `timestamp le ${eventFilter.endDateRange.toISOString().substring(0, 10)}T23:59:59.999Z`;
      if (filter.length > 0) {
        filter += ` and ${endDateFilter}`;
      } else {
        filter = `&$filter=${endDateFilter}`;
      }
    }

    if (eventFilter.startDateRange) {
      const startDateFilter = `timestamp ge ${eventFilter.startDateRange.toISOString().substring(0, 10)}T00:00:00.000Z`;
      if (filter.length > 0) {
        filter += ` and ${startDateFilter}`;
      } else {
        filter = `&$filter=${startDateFilter}`;
      }
    }

    // if there is not start and end date only get events from the last 24 hours
    if (!eventFilter.endDateRange && !eventFilter.startDateRange) {
      restAPI += `&timespan=PT24H`;
    }

    restAPI += filter;
    restAPI += `&api_key=${restAPIAppKey}`;

    const result = <any>await firstValueFrom(this.http.get(restAPI));

    if (result && result.value) {
      return result;
    } else {
      return null;
    }
  }

  private async configureAppInsights(buildVersionNumber: string) {
    try {
      if (!this.appInsights && this._appInsightsConfigured === null) {
        const connectionstring = <string>await firstValueFrom(this.configService.getConfigValue(ConfigService.APPINSIGHTS_CONNECTIONSTRING));

        this.config.loggingLevelTelemetry = 0;
        /* MK this is to sort out the 'freezing screen issue', long term suggestion is to review our log policy  (interceptor) every api call is logged */
        this.config.enableDebug = false;

        // only set up once and only if we have a key
        if (connectionstring) {
          this.config.connectionString = connectionstring;
          // this.appInsights.downloadAndSetup(this.config);
          this.appInsights = new ApplicationInsights({ config: this.config });
          this.appInsights.addTelemetryInitializer(function (envelope) {
            envelope.tags['ai.cloud.role'] = 'ClientApp';
            envelope.tags['ai.application.ver'] = buildVersionNumber;
          });

          this._appInsightsConfigured = true;
        } else {
          this._appInsightsConfigured = false;
        }
      }
    } catch {
      this._appInsightsConfigured = false;
    }
  }

  // https://geeklearning.io/monitor-your-angular-app-with-application-insights/
  private addGlobalProperties(properties?: { [key: string]: string }): { [key: string]: string } {
    if (!properties) {
      properties = {};
    }

    return properties;
  }

  private getActivatedComponent(snapshot: ActivatedRouteSnapshot): any {
    if (snapshot.firstChild) {
      return this.getActivatedComponent(snapshot.firstChild);
    }

    return snapshot.component;
  }

  private getRouteTemplate(snapshot: ActivatedRouteSnapshot): string {
    let path = '';
    if (snapshot.routeConfig) {
      path += snapshot.routeConfig.path;
    }

    if (snapshot.firstChild) {
      return path + this.getRouteTemplate(snapshot.firstChild);
    }

    return path;
  }
}
